import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import {
    LIMITES_META_INTERACTIVO_UX,
    NODO_TIPO,
    NODO_TIPO_AYUDA,
    NODO_TIPO_OPCIONES,
    NodoTipo,
} from 'src/app/models/notificacion/whatsapp_flujo.model';
import { construirFormGroupOpcion, opcionesFormArray, sincronizarOpcionesConTipo } from '../../flujo-form.util';

/** Un nodo elegible como destino en el `p-dropdown` de una opción (D9). */
export interface INodoDestinoDisponible {
    id: string;
    label: string;
}

/**
 * `p-sidebar` con el sub-formulario reactivo del paso seleccionado (S4.5).
 * NO es dueño del `FormGroup`: recibe el `FormGroup` del nodo ya armado por
 * `flujo-editor` (vía `construirFormGroupNodo`, `flujo-form.util`)
 * y solo agrega/quita controles al `FormArray` de opciones que ya vive
 * dentro de ese mismo árbol — sigue habiendo un único dueño del formulario
 * raíz (design.md: "single FormGroup owner").
 *
 * El destino de cada opción SIEMPRE sale de un `p-dropdown` con los pasos ya
 * creados, nunca de un campo libre (D9).
 *
 * Los ids no se editan a mano: renombrar el id de un nodo dejaría los destinos
 * que lo referencian apuntando a un paso inexistente, sin aviso. El id de una
 * opción se deriva de su etiqueta, que es lo que Meta devuelve al tocar el botón.
 */
@Component({
    selector: 'app-flujo-nodo-editor',
    templateUrl: './flujo-nodo-editor.component.html',
})
export class FlujoNodoEditorComponent {

    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    @Input() nodoForm: FormGroup | null = null;
    @Input() nodosDisponibles: INodoDestinoDisponible[] = [];
    @Input() esInicial = false;

    readonly NODO_TIPO = NODO_TIPO;
    readonly tiposDeNodo = NODO_TIPO_OPCIONES;
    readonly limites = LIMITES_META_INTERACTIVO_UX;

    constructor(private fb: FormBuilder) {}

    get esChoice(): boolean {
        return this.nodoForm?.get('tipo')?.value === NODO_TIPO.CHOICE;
    }

    get esMessage(): boolean {
        return this.nodoForm?.get('tipo')?.value === NODO_TIPO.MESSAGE;
    }

    get opciones() {
        return this.nodoForm ? opcionesFormArray(this.nodoForm) : null;
    }

    /** Meta acepta hasta 3 botones de respuesta rápida por mensaje; pasarse es un 4xx al enviar. */
    get puedeAgregarBoton(): boolean {
        return (this.opciones?.length || 0) < this.limites.BOTONES_MAX_REPLY;
    }

    get titulo(): string {
        return this.esInicial ? 'Primer paso' : 'Editar paso';
    }

    /** Ayuda del tipo elegido, para que el selector no sea solo cuatro nombres. */
    get ayudaDelTipo(): string {
        const tipo = this.nodoForm?.get('tipo')?.value as NodoTipo;
        return NODO_TIPO_AYUDA[tipo] || '';
    }

    /**
     * `texto` significa algo distinto en cada tipo. En el paso inicial NO es el
     * mensaje de la campaña: es lo que el bot repite si el cliente escribe en vez
     * de tocar un botón (`transicion.ts`, `resultadoReenvioOpciones`).
     */
    get etiquetaTexto(): string {
        if (this.esInicial) {
            return 'Si el cliente escribe en vez de tocar un botón, ¿qué le contesta?';
        }

        switch (this.nodoForm?.get('tipo')?.value as NodoTipo) {
            case NODO_TIPO.CHOICE:
                return 'La pregunta que le hace al cliente';
            case NODO_TIPO.HANDOFF:
                return 'Qué le avisa antes de pasarlo con una persona';
            case NODO_TIPO.END:
                return 'El mensaje con el que cierra la conversación';
            default:
                return 'El mensaje que le manda al cliente';
        }
    }

    get ayudaTexto(): string {
        return this.esInicial
            ? 'El mensaje de la campaña ya se envió con la plantilla. Esto es solo para cuando el cliente responde cualquier otra cosa: el bot le repite este texto junto con los mismos botones.'
            : '';
    }

    onTipoChange(): void {
        if (!this.nodoForm) {
            return;
        }
        sincronizarOpcionesConTipo(this.fb, this.nodoForm);
    }

    agregarOpcion(): void {
        this.opciones?.push(construirFormGroupOpcion(this.fb));
    }

    quitarOpcion(indice: number): void {
        this.opciones?.removeAt(indice);
    }

    /** El id del botón es lo que Meta devuelve al tocarlo: se deriva de la etiqueta, no se tipea aparte. */
    onEtiquetaOpcionChange(indice: number): void {
        const opcion = this.opciones?.at(indice);
        if (!opcion) {
            return;
        }

        const etiqueta = String(opcion.get('etiqueta')?.value || '').trim();
        opcion.get('id')?.setValue(etiqueta);
    }

    cerrar(): void {
        this.visible = false;
        this.visibleChange.emit(false);
    }

    etiquetaTipo(tipo: NodoTipo): string {
        return this.tiposDeNodo.find((opcion) => opcion.value === tipo)?.label || '';
    }
}
